import './coapplicantUnemploymentTypePage.scss';

import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import { EmploymentType } from '@thesavingsgroup/enums/EmploymentType';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { progress },
    next,
    previous,
  } = useWorkflowStore();

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentIncomeEmploymentType,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentIncomeEmploymentType,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      employmentTypeValue: coapplicantCurrentIncomeEmploymentType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<EmploymentType>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const employmentTypeValue = event.target.value;

    setIsInitialSelection(
      isEmpty(coapplicantCurrentIncomeEmploymentType) &&
        isValidClickEvent(event),
    );

    setCoapplicantCurrentIncomeEmploymentType(employmentTypeValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { employmentTypeValue } = values;

    if (!employmentTypeValue) {
      return;
    }

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-unemployment-type-page" sx={theme.pageLayout}>
        <Header
          progress={progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantUnemploymentType.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={locator.pages.coapplicantUnemploymentType.form.h1}
              >
                {t('CoapplicantUnemploymentTypePage.title')}
              </h1>
              <h2
                data-testid={locator.pages.coapplicantUnemploymentType.form.h2}
              >
                {t('CoapplicantUnemploymentTypePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantUnemploymentType.alert.error
                    }
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  name="employmentTypeValue"
                  options={[
                    {
                      label: t('CoapplicantUnemploymentTypePage.disability'),
                      value: EmploymentType.DISABILITY,
                      testId:
                        locator.pages.coapplicantUnemploymentType.form
                          .radioOption1,
                    },
                    {
                      label: t('CoapplicantUnemploymentTypePage.homemaker'),
                      value: EmploymentType.HOMEMAKER,
                      testId:
                        locator.pages.coapplicantUnemploymentType.form
                          .radioOption2,
                    },
                    {
                      label: t('CoapplicantUnemploymentTypePage.retired'),
                      value: EmploymentType.RETIRED,
                      testId:
                        locator.pages.coapplicantUnemploymentType.form
                          .radioOption3,
                    },
                    {
                      label: t('CoapplicantUnemploymentTypePage.student'),
                      value: EmploymentType.STUDENT,
                      testId:
                        locator.pages.coapplicantUnemploymentType.form
                          .radioOption4,
                    },
                    {
                      label: t('CoapplicantUnemploymentTypePage.other'),
                      value: EmploymentType.OTHER,
                      testId:
                        locator.pages.coapplicantUnemploymentType.form
                          .radioOption5,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.coapplicantUnemploymentType.form
                      .radioGroupRoot
                  }
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantUnemploymentType.form.submitButton
                  }
                  disabled={isEmpty(coapplicantCurrentIncomeEmploymentType)}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantUnemploymentTypePage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantUnemploymentType.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantUnemploymentTypePage';
